/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 *
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 *
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains the patient summary component
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * React
 */
import * as React from 'react';

import { makeStyles, Theme, useTheme } from '@material-ui/core/styles';

import { faUser } from '@fortawesome/pro-duotone-svg-icons/faUser';

import { faHeartbeat } from '@fortawesome/pro-duotone-svg-icons/faHeartbeat';

import { faBuilding } from '@fortawesome/pro-duotone-svg-icons/faBuilding';

import Grid from '@material-ui/core/Grid';

/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

import * as Dtos from '../../api/Neon.Dtos';
import InformationCard from '../InformationCard/InformationCard';
import { PatientContext, InstitutionContext, IPatient } from '@ngt/online-patient-management-ui';
import { DateTime } from 'luxon';


/*
 * ---------------------------------------------------------------------------------
 * interfaces
 * ---------------------------------------------------------------------------------
 */

interface IPatientInformationProps {
    patient?: IPatient | null;
}

/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */

const useStyles = makeStyles<Theme>(theme => ({
    item: {
        display: 'flex',

        '& > *': {
            flex: '1 1 auto'
        }
    }
}));

/*
 * ---------------------------------------------------------------------------------
 * Constants
 * ---------------------------------------------------------------------------------
 */


const PatientInformation: React.FunctionComponent<IPatientInformationProps> = ({
    patient
}) => {

    const theme = useTheme()
    const classes = useStyles();
    const [contextPatient] = React.useContext(PatientContext);
    const [institution] = React.useContext(InstitutionContext);

    const typedPatient: Dtos.Patient | null = (patient ?? contextPatient) as any;

    const status = React.useMemo(() => {
        switch (typedPatient?.status) {
            case Dtos.PatientStatuses.Screened:
                return 'Screened'
            case Dtos.PatientStatuses.Registered:
                return 'Registered'
            case Dtos.PatientStatuses.Randomised:
                return `Randomised - Arm ${typedPatient?.treatmentId === 1 ? 'A' : (typedPatient?.treatmentId === 2 ? 'B' : 'C')}`
            case Dtos.PatientStatuses.Waivered:
                return 'Waivered'
            case Dtos.PatientStatuses.Ineligible:
                return 'Ineligible'
            case Dtos.PatientStatuses.Removed:
                return 'Removed'
            case Dtos.PatientStatuses.NewPatient:
            default:
                return 'New Patient'
        }
    }, [typedPatient?.status, typedPatient?.treatmentId])

    return (
        <Grid
            container
            spacing={3}
        >
            <Grid
                item
                xs={12}
                md={4}
                className={classes.item}
            >
                <InformationCard
                    color={theme.palette.primary.main}
                    icon={faUser}
                    title="Patient"
                    text={typedPatient?.studyNumber ?? 'New Patient'}
                    caption={typedPatient?.initials}
                />
            </Grid>
            <Grid
                item
                xs={12}
                md={4}
                className={classes.item}
            >
                <InformationCard
                    color={theme.palette.secondary.main}
                    icon={faBuilding}
                    title="Institution"
                    text={institution?.code}
                    caption={institution?.name}
                />
            </Grid>
            <Grid
                item
                xs={12}
                md={4}
                className={classes.item}
            >
                <InformationCard
                    color="#76c6d7"
                    icon={faHeartbeat}
                    title="Status"
                    text={status}
                    caption={DateTime.fromISO(typedPatient?.randomisationDate ?? typedPatient?.registrationDate ?? typedPatient?.enteredDate ?? DateTime.local().toISO({ includeOffset: false  })).toFormat('dd/MM/yyyy')}
                />
            </Grid>
        </Grid>
    );
};

/*
 * ---------------------------------------------------------------------------------
 * Default Export
 * ---------------------------------------------------------------------------------
 */

export default PatientInformation;
